/**
 * SuppressIssueDialog component
 *
 * A shadcn/ui Dialog for creating suppression rules based on the current issue.
 * Pre-populates match criteria from the issue and lets the user toggle which
 * criteria to include in the rule.
 */

import { useState, useMemo, useDeferredValue } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateSuppressionRule, useSuppressionRulePreview } from "@/hooks/useSuppressionRules";
import type { SecurityIssue } from "@/types/security";

interface SuppressIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: SecurityIssue;
  numericIssueId: number;
}

type ExpiryOption = "30" | "90" | "never";

export function SuppressIssueDialog({
  open,
  onOpenChange,
  issue,
  numericIssueId,
}: SuppressIssueDialogProps) {
  const createMutation = useCreateSuppressionRule();

  // Match criteria toggles
  const [matchIssueType, setMatchIssueType] = useState(true);
  const [matchSeverity, setMatchSeverity] = useState(false);
  const [matchScanner, setMatchScanner] = useState(false);
  const [matchTitle, setMatchTitle] = useState(false);

  // Rule fields
  const [ruleName, setRuleName] = useState("");
  const [reason, setReason] = useState("");
  const [expiryOption, setExpiryOption] = useState<ExpiryOption>("never");

  // Derive a default rule name based on toggles
  const defaultRuleName = useMemo(() => {
    const parts: string[] = [];
    if (matchIssueType) parts.push(issue.type);
    if (matchSeverity) parts.push(issue.severity);
    if (matchScanner && issue.scanner) parts.push(issue.scanner);
    if (parts.length === 0) return `Suppress issues like ${issue.id}`;
    return `Suppress ${parts.join(" ")} issues`;
  }, [matchIssueType, matchSeverity, matchScanner, issue]);

  const effectiveName = ruleName.trim() || defaultRuleName;

  // Build preview text
  const previewParts: string[] = [];
  if (matchIssueType) previewParts.push(`type "${issue.type}"`);
  if (matchSeverity) previewParts.push(`severity "${issue.severity}"`);
  if (matchScanner && issue.scanner) previewParts.push(`scanner "${issue.scanner}"`);
  if (matchTitle) previewParts.push(`title matching "${issue.title}"`);

  const previewText =
    previewParts.length > 0
      ? `This rule will suppress future issues with ${previewParts.join(", ")}.`
      : "No match criteria selected. At least one criterion is required.";

  const hasAnyCriteria = matchIssueType || matchSeverity || matchScanner || matchTitle;

  const previewCriteria = useMemo(() => ({
    match_issue_type: matchIssueType ? issue.type : null,
    match_severity: matchSeverity ? issue.severity : null,
    match_scanner: matchScanner && issue.scanner ? issue.scanner : null,
    match_title_pattern: matchTitle ? `*${issue.title}*` : null,
  }), [matchIssueType, matchSeverity, matchScanner, matchTitle, issue]);

  const deferredCriteria = useDeferredValue(previewCriteria);
  const previewQuery = useSuppressionRulePreview(deferredCriteria, hasAnyCriteria && open);

  const handleSubmit = () => {
    if (!hasAnyCriteria) return;

    // Calculate expires_at
    let expiresAt: string | null = null;
    if (expiryOption !== "never") {
      const days = parseInt(expiryOption, 10);
      const date = new Date();
      date.setDate(date.getDate() + days);
      expiresAt = date.toISOString();
    }

    createMutation.mutate(
      {
        name: effectiveName,
        match_issue_type: matchIssueType ? issue.type : null,
        match_severity: matchSeverity ? issue.severity : null,
        match_scanner: matchScanner && issue.scanner ? issue.scanner : null,
        match_title_pattern: matchTitle ? `*${issue.title}*` : null,
        expires_at: expiresAt,
        reason: reason.trim() || null,
        source_issue_id: numericIssueId > 0 ? numericIssueId : null,
      },
      {
        onSuccess: () => {
          // Reset form and close
          setRuleName("");
          setReason("");
          setExpiryOption("never");
          setMatchIssueType(true);
          setMatchSeverity(false);
          setMatchScanner(false);
          setMatchTitle(false);
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Create Suppression Rule</DialogTitle>
          <DialogDescription>
            Suppress future security issues matching the criteria below. Issues matching this rule
            will not be created during security scanning.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0 p-1">
          {/* Rule Name */}
          <div className="space-y-2">
            <Label htmlFor="rule-name">Rule Name</Label>
            <Input
              id="rule-name"
              placeholder={defaultRuleName}
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
            />
          </div>

          {/* Match Criteria */}
          <div className="space-y-3">
            <Label>Match Criteria</Label>
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="match-issue-type"
                  checked={matchIssueType}
                  onCheckedChange={(checked) => setMatchIssueType(checked === true)}
                />
                <Label htmlFor="match-issue-type" className="text-sm font-normal">
                  Issue Type: <span className="font-medium">{issue.type}</span>
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="match-severity"
                  checked={matchSeverity}
                  onCheckedChange={(checked) => setMatchSeverity(checked === true)}
                />
                <Label htmlFor="match-severity" className="text-sm font-normal">
                  Severity: <span className="font-medium">{issue.severity}</span>
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="match-scanner"
                  checked={matchScanner}
                  onCheckedChange={(checked) => setMatchScanner(checked === true)}
                />
                <Label htmlFor="match-scanner" className="text-sm font-normal">
                  Scanner: <span className="font-medium">{issue.scanner || "N/A"}</span>
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="match-title"
                  checked={matchTitle}
                  onCheckedChange={(checked) => setMatchTitle(checked === true)}
                />
                <Label htmlFor="match-title" className="text-sm font-normal">
                  Title Pattern: <span className="font-medium truncate">{issue.title}</span>
                </Label>
              </div>
            </div>
          </div>

          {/* Expiry */}
          <div className="space-y-2">
            <Label htmlFor="expiry">Expiry</Label>
            <Select value={expiryOption} onValueChange={(v) => setExpiryOption(v as ExpiryOption)}>
              <SelectTrigger id="expiry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="never">Never (permanent)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              placeholder="Why is this issue being suppressed? e.g., Known test traffic, internal tooling, etc."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>

          {/* Preview */}
          <div className="rounded-md bg-muted p-3">
            <p className="text-sm text-muted-foreground">{previewText}</p>
            {hasAnyCriteria && previewQuery.data && (
              <div className="mt-2 border-t pt-2">
                <p className="text-sm font-medium">
                  {previewQuery.data.total_count} existing open issue{previewQuery.data.total_count !== 1 ? "s" : ""} would match
                </p>
                {previewQuery.data.sample_issues.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {previewQuery.data.sample_issues.map((si) => (
                      <li key={si.id} className="text-xs text-muted-foreground truncate">
                        [{si.severity}] {si.title}
                      </li>
                    ))}
                    {previewQuery.data.total_count > previewQuery.data.sample_issues.length && (
                      <li className="text-xs text-muted-foreground italic">
                        ...and {previewQuery.data.total_count - previewQuery.data.sample_issues.length} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
            {hasAnyCriteria && previewQuery.isLoading && (
              <p className="mt-2 text-xs text-muted-foreground">Loading preview...</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasAnyCriteria || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
